import React, { useEffect, useState, useCallback } from "react";
import { getSocket } from "@/lib/socket";
import { Search, Phone, MapPin, Printer, Loader2, Trash2, MessageSquare, AlertTriangle, Download } from "lucide-react";
import api, { money } from "@/lib/api";
import { printCustomerReceipt, printKitchenTicket } from "@/lib/receipt";
import OrderChatModal from "@/components/OrderChatModal";
import { useSettings } from "@/context/SettingsContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["NEW", "ACCEPTED", "PREPARING", "READY", "OUT_FOR_DELIVERY", "COMPLETED", "CANCELLED"];
const STALE_MINUTES = 120; // active orders older than this need attention
const ACTIVE_STATUSES = ["NEW", "ACCEPTED", "PREPARING", "READY", "OUT_FOR_DELIVERY"];
const isStale = (o) => ACTIVE_STATUSES.includes(o.status) && (Date.now() - new Date(o.created_at).getTime()) > STALE_MINUTES * 60000;

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dateBounds = (range, from, to) => {
  const today = new Date();
  if (range === "today") return { from: ymd(today), to: ymd(today) };
  if (range === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: ymd(y), to: ymd(y) }; }
  if (range === "7d") { const s = new Date(today); s.setDate(s.getDate() - 6); return { from: ymd(s), to: ymd(today) }; }
  if (range === "custom") return { from: from || null, to: to || null };
  return {};
};

const csvCell = (v) => {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const exportCsv = (orders) => {
  const head = ["Order #", "Date", "Time", "Status", "Type", "Payment", "Customer", "Phone", "Items", "Subtotal", "Delivery fee", "Discount", "Total"];
  const rows = orders.map((o) => {
    const d = new Date(o.created_at);
    const items = (o.items || []).map((i) => `${i.qty || i.quantity || 1}x ${i.name}`).join("; ");
    const discount = Number(o.discount || 0) + Number(o.points_discount || 0) + Number(o.reward_discount || 0);
    return [o.order_number, d.toLocaleDateString("en-GB"), d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }), o.status, o.type || "", o.payment_method || "",
      o.customer?.name || "", o.customer?.phone || "", items, Number(o.subtotal || 0).toFixed(2), Number(o.delivery_fee || 0).toFixed(2), discount.toFixed(2), Number(o.total || 0).toFixed(2)];
  });
  const csv = [head, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `metros-orders-${ymd(new Date())}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
};
const staleAge = (o) => {
  const mins = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
  return mins >= 120 ? `${Math.floor(mins / 60)}h` : `${mins}m`;
};
const COLOR = {
  NEW: "bg-blue-100 text-blue-700", ACCEPTED: "bg-cyan-100 text-cyan-700", PREPARING: "bg-amber-100 text-amber-700",
  READY: "bg-purple-100 text-purple-700", OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-700",
  COMPLETED: "bg-green-100 text-green-700", CANCELLED: "bg-red-100 text-red-700",
};

function PayBadge({ order }) {
  const ps = order.payment_status || (order.payment_method === "card" ? "pending" : "cash");
  const map = { paid: ["Paid", "bg-green-100 text-green-700"], pending: ["Unpaid", "bg-amber-100 text-amber-700"], failed: ["Payment failed", "bg-red-100 text-red-700"], cash: ["Cash", "bg-gray-100 text-gray-600"] };
  const [label, cls] = map[ps] || map.cash;
  return <span data-testid={`pay-badge-${order.order_number}`} className={`text-[10px] font-bold px-2 py-1 rounded-full ${cls}`}>{label}</span>;
}

export default function AdminOrders() {
  const { settings } = useSettings();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [typeF, setTypeF] = useState("");
  const [payF, setPayF] = useState("");
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({});
  const [chatOrder, setChatOrder] = useState(null);
  const loadUnread = useCallback(() => { api.get("/admin/chat/unread-orders").then(({ data }) => setUnread(data || {})).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filter !== "ALL") q.set("status", filter);
    if (search) q.set("search", search);
    if (typeF) q.set("type", typeF);
    if (payF) q.set("payment", payF);
    const d = dateBounds(range, from, to);
    if (d.from) q.set("date_from", d.from);
    if (d.to) q.set("date_to", d.to);
    q.set("_", Date.now());
    api.get(`/admin/orders?${q.toString()}`).then(({ data }) => { setOrders(data); setLoading(false); });
  }, [filter, search, typeF, payF, range, from, to]);

  useEffect(() => { load(); loadUnread(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load, loadUnread]);
  useEffect(() => {
    const s = getSocket();
    const onEvt = () => load();
    const onChat = () => loadUnread();
    s.on("new_order", onEvt);
    s.on("order_updated", onEvt);
    s.on("chat_message", onChat);
    return () => { s.off("new_order", onEvt); s.off("order_updated", onEvt); s.off("chat_message", onChat); };
  }, [load, loadUnread]);

  const changeStatus = async (id, status) => {
    const { data } = await api.patch(`/admin/orders/${id}/status`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
    if (sel?.id === id) setSel(data);
    toast.success(`Order marked ${status.replace(/_/g, " ")}`);
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.order_number}? This permanently removes it and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      if (sel?.id === o.id) setSel(null);
      toast.success(`Order ${o.order_number} deleted`);
    } catch { toast.error("Could not delete order"); }
  };

  const printOrder = (o) => printCustomerReceipt(o, settings);

  const printKitchen = (o) => printKitchenTicket(o);

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl mb-1">Orders</h1>
      <p className="text-sm text-muted-foreground mb-5">Manage and track all orders.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="admin-order-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order #, name, phone…" className="w-full bg-white border border-gray-200 rounded-full pl-9 pr-4 py-2.5 text-sm" />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {["ALL", ...STATUSES].map((s) => (
            <button key={s} data-testid={`order-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${filter === s ? "bg-metros-black text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{s.replace(/_/g, " ")}</button>
          ))}
        </div>
      </div>

      <div data-testid="order-filters" className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="order-filter-range" value={range} onChange={(e) => setRange(e.target.value)} className="bg-white border border-gray-200 rounded-full px-3 py-2 text-xs font-semibold text-gray-700">
          <option value="all">All dates</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7d">Last 7 days</option>
          <option value="custom">Custom range</option>
        </select>
        {range === "custom" && (
          <>
            <input data-testid="order-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs" />
            <span className="text-xs text-gray-400">to</span>
            <input data-testid="order-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs" />
          </>
        )}
        <select data-testid="order-filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)} className="bg-white border border-gray-200 rounded-full px-3 py-2 text-xs font-semibold text-gray-700">
          <option value="">All types</option>
          <option value="delivery">Delivery</option>
          <option value="collection">Collection</option>
        </select>
        <select data-testid="order-filter-payment" value={payF} onChange={(e) => setPayF(e.target.value)} className="bg-white border border-gray-200 rounded-full px-3 py-2 text-xs font-semibold text-gray-700">
          <option value="">All payments</option>
          <option value="cash">Cash</option>
          <option value="card">Card</option>
        </select>
        {(typeF || payF || range !== "all") && (
          <button data-testid="order-filter-clear" onClick={() => { setTypeF(""); setPayF(""); setRange("all"); setFrom(""); setTo(""); }} className="text-xs font-semibold text-metros-red px-2">Clear filters</button>
        )}
        <span data-testid="order-filter-count" className="ml-auto text-xs text-muted-foreground">{orders.length} order{orders.length === 1 ? "" : "s"}</span>
        <button data-testid="order-export-csv" disabled={!orders.length} onClick={() => { exportCsv(orders); toast.success(`Exported ${orders.length} orders`); }} className="flex items-center gap-1.5 bg-metros-black text-white rounded-full px-3 py-2 text-xs font-semibold disabled:opacity-40"><Download size={14} /> Export CSV</button>
      </div>

      {(() => { const n = orders.filter(isStale).length; return n > 0 ? (
        <div data-testid="stale-orders-alert" className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 text-sm">
          <AlertTriangle size={16} className="text-amber-600 shrink-0" />
          <span><b>{n}</b> active order{n > 1 ? "s have" : " has"} been waiting over {STALE_MINUTES / 60}h. Please complete or cancel {n > 1 ? "them" : "it"} so reward cycles stay accurate.</span>
        </div>
      ) : null; })()}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-metros-red" /></div> : orders.length === 0 ? (
        <p className="text-center text-muted-foreground py-16">No orders found.</p>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.order_number}`} className={`bg-white border rounded-2xl p-4 ${isStale(o) ? "border-amber-300 ring-1 ring-amber-200 bg-amber-50/40" : "border-gray-100"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${COLOR[o.status]}`}>{o.status.replace(/_/g, " ")}</span>
                  {isStale(o) && (
                    <span data-testid={`stale-badge-${o.order_number}`} title={`Waiting ${staleAge(o)} — needs action`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                      <AlertTriangle size={11} /> {staleAge(o)}
                    </span>
                  )}
                  <PayBadge order={o} />
                  <div><p className="font-heading font-bold">{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.type}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold">{money(o.total)}</span>
                  <button onClick={() => printOrder(o)} className="p-2 rounded-full hover:bg-gray-100" title="Customer receipt" data-testid={`print-${o.order_number}`}><Printer size={16} /></button>
                  <button onClick={() => printKitchen(o)} className="p-2 rounded-full hover:bg-gray-100 text-xs font-bold" title="Kitchen ticket" data-testid={`print-kitchen-${o.order_number}`}>K</button>
                  <button onClick={() => setChatOrder(o)} className="relative p-2 rounded-full hover:bg-gray-100" title="Chat with customer" data-testid={`chat-order-${o.order_number}`}>
                    <MessageSquare size={16} />
                    {unread[o.order_number] > 0 && <span data-testid={`chat-unread-${o.order_number}`} className="absolute -top-0.5 -right-0.5 bg-metros-red text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{unread[o.order_number]}</span>}
                  </button>
                  <button onClick={() => setSel(sel?.id === o.id ? null : o)} className="text-sm font-semibold text-metros-red">{sel?.id === o.id ? "Hide" : "Details"}</button>
                  <button onClick={() => deleteOrder(o)} className="p-2 rounded-full hover:bg-red-50 text-gray-400 hover:text-metros-red transition-colors" title="Delete order" data-testid={`delete-order-${o.order_number}`}><Trash2 size={16} /></button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-sm text-muted-foreground flex items-center gap-1"><Phone size={13} /> {o.customer?.name} · {o.customer?.phone}</span>
                <div className="ml-auto w-44">
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger data-testid={`status-select-${o.order_number}`} className="h-9 rounded-full bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {sel?.id === o.id && (
                <div className="mt-4 border-t border-gray-100 pt-4 grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-bold mb-2">Items</h4>
                    {o.items.map((i, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="flex justify-between"><span>{i.quantity}× {i.name}</span><span>{money(i.line_total)}</span></div>
                        {i.options?.length > 0 && (
                          <div className="mt-0.5">
                            {i.options.map((op, oi) => (
                              <div key={oi} className="flex justify-between text-xs text-muted-foreground pl-3">
                                <span>+ {op.name}</span><span>{money(op.price || 0)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {i.special_instructions && <p className="text-xs italic text-muted-foreground">“{i.special_instructions}”</p>}
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">Fulfilment</h4>
                    {o.address && <p className="flex items-start gap-1 text-muted-foreground"><MapPin size={13} className="mt-0.5" /> {o.address.line1}, {o.address.postcode}</p>}
                    {o.delivery_instructions && <p className="text-muted-foreground">Instructions: {o.delivery_instructions}</p>}
                    <p className="text-muted-foreground">Placed: {new Date(o.created_at).toLocaleString()}</p>
                    {o.collection_time && o.collection_time !== "asap"
                      ? <p className="font-semibold text-metros-black" data-testid={`sched-${o.order_number}`}>Scheduled {o.type === "delivery" ? "delivery" : "collection"}: {o.collection_time}</p>
                      : <p className="text-muted-foreground">{o.type === "delivery" ? "Delivery" : "Collection"}: ASAP</p>}
                    {o.note && <p className="text-muted-foreground mt-1">Note: {o.note}</p>}
                    {o.promo_code && <p className="text-metros-red">Promo: {o.promo_code}</p>}
                    <a href={`tel:${o.customer?.phone}`} className="inline-flex items-center gap-1 mt-2 bg-metros-black text-white rounded-full px-3 py-1.5 text-xs font-semibold"><Phone size={12} /> Call customer</a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {chatOrder && <OrderChatModal order={chatOrder} onClose={() => { setChatOrder(null); loadUnread(); }} />}
    </div>
  );
}
